import React from "react";

// Source
import StoreRole from "../../store/Role";
import StoreRoute from "../../store/Route";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelRole from "../../model/Role";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ApiTeam from "../../api/custom/Team";
import * as ModelTeam from "../../model/custom/Team";
import ControllerAlert from "../Alert";

export default class ControllerTeamProfile {
    // Variable
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementUserId = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelTeam.Idata | undefined>(undefined);
    private dataMain = React.useRef<ModelTeam.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelTeam.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelTeam.IselectOption | undefined>>;

    getDataMain: ModelTeam.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelTeam.Idata | undefined>>;

    getValidationForm: ModelTeam.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelTeam.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementUserIdValue: string;
    setElementUserIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelTeam.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getDataMain, setDataMain] = React.useState<ModelTeam.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getValidationForm, setValidationForm] = React.useState<ModelTeam.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementUserIdValue, setElementUserIdValue] = React.useState<string>("");
        this.getElementUserIdValue = getElementUserIdValue;
        this.setElementUserIdValue = setElementUserIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_team_profile", () => {
                this.initializeHtmlElement();
            });

            const historyState = window.history.state as Record<string, unknown> | undefined;

            if (historyState) {
                const parameterList = historyState.parameterList as ModelTeam.Idata;

                if (parameterList) {
                    this.dataMain.current = { ...parameterList };
                } else {
                    this.dataMain.current = {} as ModelTeam.Idata;
                }
            } else {
                this.dataMain.current = {} as ModelTeam.Idata;
            }

            this.setDataMain(this.dataMain.current);

            this.apiValidationForm();

            this.apiSelectOption();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRoleData.current, this.storageRouteData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        this.setApiLoadingList((prev) => ({
            ...prev,
            profileChange: true
        }));

        if (this.dataMain.current && this.dataMain.current.id) {
            const id = this.dataMain.current.id;
            const name = this.readHtmlElementValue("name");
            const member = this.dataMain.current.member || "";

            ApiTeam.updateData(id, name, member, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    profileChange: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.controllerAlert.show("success", response.message.success, true);
            });
        } else {
            const member = this.dataMain.current && this.dataMain.current.member ? this.dataMain.current.member : "";

            ApiTeam.createData(this.readHtmlElementValue("name"), member, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    profileChange: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.controllerAlert.show("success", response.message.success, true);
            });
        }
    };

    clickAddMember = (): void => {
        if (this.dataMain.current && this.elementUserId.current && this.elementUserId.current.previousElementSibling) {
            const userId = this.elementUserId.current.value;
            const elementSibling = this.elementUserId.current.previousElementSibling.querySelector<HTMLInputElement>(
                "input[type='text']"
            ) as HTMLInputElement;
            const userName = elementSibling.value;

            if (!this.dataMain.current.member) {
                this.dataMain.current.member = {};
            }

            this.dataMain.current.member = {
                ...this.dataMain.current.member,
                [userId]: userName
            };

            const dataMain = {
                ...this.dataMain.current,
                member: this.dataMain.current.member
            } as ModelTeam.Idata;

            this.setDataMain(dataMain);
        }
    };

    clickRemove = (userId): void => {
        if (this.dataMain.current) {
            delete this.dataMain.current.member[userId];

            const dataMain = {
                ...this.dataMain.current,
                member: this.dataMain.current.member
            } as ModelTeam.Idata;

            this.setDataMain(dataMain);
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementUserId.current = document.querySelector<HTMLInputElement>("[name='user_id']");
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_team_profile");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiTeam.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelTeam.IselectOption);
        });
    };

    private readHtmlElementValue = (tag: string): string => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.value : "";
    };

    private apiValidationForm = (): void => {
        ApiTeam.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelTeam.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };
}
